/**
 * FO-380 (spec 83) — cursusdag levert altijd een werkbon(dag) op.
 *
 * Types voor de aanvullend-idempotente werkbon-generator vanuit een
 * cursus-sessie: per actieve deelname worden ontbrekende cursusdagen als
 * dagregels aangevuld op de bestaande bon, of er komt een (vervolg-)bon.
 * Het resultaat is per deelnemer zichtbaar (FO-338-patroon: niets stil).
 *
 * Gedeeld tussen:
 *   - POST /opleiding-sessies/:id/werkbonnen-aanvullen (handmatig vangnet)
 *   - POST /opleiding-sessies/:id/dagen (dag toevoegen → automatische aanvulling)
 *   - de backoffice-resultaatweergave op de sessie-detail.
 */

import { z } from 'zod'
import { idParam } from './id-validators'

/**
 * Wat er per deelnemer gebeurde bij het aanvullen:
 *   - BON_AANGEMAAKT      → er was geen enkele bon; nieuwe bon met álle cursusdagen.
 *   - AANGEVULD           → bestaande (hoofd)bon kreeg de ontbrekende dagregels.
 *   - VERVOLG_AANGEVULD   → hoofdbon vergrendeld; een bestaande open vervolg-bon
 *                           kreeg de ontbrekende dagregels.
 *   - VERVOLG_AANGEMAAKT  → alle bonnen vergrendeld; nieuwe interne vervolg-bon
 *                           met de ontbrekende dag(en) en dezelfde bron-koppeling.
 *   - AL_VOLLEDIG         → alle cursusdagen waren al gedekt (idempotent, geen mutatie).
 *   - OVERGESLAGEN        → niets kunnen doen; `reden` legt uit waarom.
 */
export const WERKBON_AANVUL_RESULTATEN = [
  'BON_AANGEMAAKT',
  'AANGEVULD',
  'VERVOLG_AANGEVULD',
  'VERVOLG_AANGEMAAKT',
  'AL_VOLLEDIG',
  'OVERGESLAGEN',
] as const
export type WerkbonAanvulResultaat = (typeof WERKBON_AANVUL_RESULTATEN)[number]

export const WERKBON_AANVUL_RESULTAAT_LABELS: Record<WerkbonAanvulResultaat, string> = {
  BON_AANGEMAAKT: 'Werkbon aangemaakt',
  AANGEVULD: 'Dagregel(s) toegevoegd',
  VERVOLG_AANGEVULD: 'Vervolg-bon aangevuld',
  VERVOLG_AANGEMAAKT: 'Vervolg-bon aangemaakt',
  AL_VOLLEDIG: 'Al volledig',
  OVERGESLAGEN: 'Overgeslagen',
}

/** Per-deelnemer resultaat van een aanvul-run (FO-338-patroon: niets stil). */
export interface WerkbonAanvulDeelnemerDTO {
  deelnameId: string
  employeeId: string
  employeeNaam: string
  resultaat: WerkbonAanvulResultaat
  /** De bon die is aangemaakt of aangevuld (null bij AL_VOLLEDIG zonder bon / OVERGESLAGEN). */
  werkbonId: string | null
  werkbonNumber: string | null
  /** Toegevoegde dagregel-datums (yyyy-MM-dd), leeg bij AL_VOLLEDIG/OVERGESLAGEN. */
  toegevoegdeDatums: string[]
  /** Uitleg bij OVERGESLAGEN of context bij VERVOLG_* (bv. "hoofdbon is ingediend"). */
  reden: string | null
}

export interface WerkbonAanvulResultDTO {
  deelnemers: WerkbonAanvulDeelnemerDTO[]
  /** Globale meldingen (bv. holding-BV-fallback). */
  warnings: string[]
}

/**
 * Body van POST /opleiding-sessies/:id/werkbonnen-aanvullen.
 * `deelnameIds` weglaten = alle actieve deelnames van de sessie (INGEPLAND/
 * BEZIG/AFGEROND); meegeven = alleen die deelnames (per-deelnemer vangnet).
 */
export const werkbonnenAanvullenInputSchema = z.object({
  deelnameIds: z.array(idParam('deelnameId')).optional(),
})
export type WerkbonnenAanvullenInput = z.infer<typeof werkbonnenAanvullenInputSchema>
